import { CloseOutlined } from '@ant-design/icons'
import { message } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/Auth'

interface FormData {
  activityHighlights: string[]
  activitySummary: string
  uploadedImages: string[]
  // Step 4 fields
  packageName?: string
  shortDescription?: string
  whatsIncluded?: string[]
  whatsNotIncluded?: string[]
  importantInfo?: string[]
  pricePerPerson?: string
  pricePerChild?: string
  extraExpenses?: string
  discount?: string
  meetingPoints?: Array<{
    title: string
    subtitle: string
    lat: number | null
    long: number | null
    details: string
  }>
  itineraries?: Array<{
    title: string
    duration: string
    details: string
    hasPhoto: boolean
    images: string[]
  }>
  schedule?: string
  scheduleImages?: string[]
  scheduleHasPhoto?: boolean
  [key: string]: any
}

interface StepProps {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: FormData
  updateFormData: (data: Partial<FormData>) => void
}

const Step3: React.FC<StepProps> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [generatingAll, setGeneratingAll] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { userLoggedIn } = useAuth()

  const getDraftId = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('draft_id')
  }

  const formatSingleContent = (text: string): string => {
    if (!text || typeof text !== 'string') return ''

    let cleaned = text.trim()

    cleaned = cleaned.replace(/```[\w]*\n?/g, '')
    cleaned = cleaned.replace(/^["']|["']$/g, '')
    cleaned = cleaned.replace(/,\s*$/, '')
    cleaned = cleaned.replace(/^\s*\[\s*/, '')
    cleaned = cleaned.replace(/\s*\]\s*$/, '')
    cleaned = cleaned.replace(/\s+/g, ' ')
    cleaned = cleaned.replace(/^\{|\}$/g, '')

    return cleaned.trim()
  }

  const formatGeneratedContent = (content: any): string | string[] => {
    if (!content) return ''

    if (Array.isArray(content)) {
      return content
        .filter((item) => {
          if (typeof item !== 'string') return false
          const cleaned = item.trim()
          return (
            cleaned !== '' &&
            cleaned !== '```json' &&
            cleaned !== '```' &&
            cleaned !== '[' &&
            cleaned !== ']' &&
            cleaned !== ',' &&
            !cleaned.match(/^```\w*$/)
          )
        })
        .map((item) => formatSingleContent(item))
        .filter((item) => item.trim() !== '')
    }

    return formatSingleContent(content)
  }

  const calculateProgress = () => {
    let completedFields = 0

    const hasHighlights = formData.activityHighlights?.some((h) => h.trim() !== '')
    if (hasHighlights) {
      completedFields += 50
    }

    if (formData.activitySummary?.trim()) {
      completedFields += 50
    }

    return Math.min(completedFields, 100)
  }

  useEffect(() => {
    const progress = calculateProgress()
    onStepProgress(progress)
  }, [formData.activityHighlights, formData.activitySummary, onStepProgress])

  const handleHighlightChange = (index: number, value: string) => {
    if (value.length <= 500) {
      const newHighlights = [...(formData.activityHighlights || [])]
      newHighlights[index] = value
      updateFormData({ activityHighlights: newHighlights })
    }
  }

  const handleAddHighlight = () => {
    updateFormData({ activityHighlights: [...(formData.activityHighlights || []), ''] })
  }

  const handleRemoveHighlight = (indexToRemove: number) => {
    const newHighlights = formData.activityHighlights.filter((_, index) => index !== indexToRemove)
    updateFormData({ activityHighlights: newHighlights })
  }

  const handleSummaryChange = (value: string) => {
    if (value.length <= 500) {
      updateFormData({ activitySummary: value })
    }
  }

  const generateAllContent = async () => {
    const draftId = getDraftId()
    if (!draftId) {
      console.error('Draft ID not found in URL')
      message.error('Draft ID not found. Please try again.')
      return
    }

    if (!userLoggedIn?.userToken) {
      console.error('Authentication token is missing. Please log in again.')
      message.error('Authentication token is missing. Please log in again.')
      return
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/expert-submission/draft/${draftId}/step/3/generate-content`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: userLoggedIn.userToken,
          },
        },
      )

      if (!response.ok) {
        throw new Error(`Failed to generate content: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      console.log('API Response:', result)

      if (!result.result) {
        throw new Error(result.message || 'Failed to generate content')
      }

      const updates: Partial<FormData> = {}

      // Handle highlights
      if (result.data?.highlight) {
        const rawHighlights = Array.isArray(result.data.highlight)
          ? result.data.highlight
          : [result.data.highlight]

        const formattedHighlights = formatGeneratedContent(rawHighlights) as string[]
        updates.activityHighlights = formattedHighlights.filter((h) => h.trim() !== '')
      }

      // Handle description/summary
      if (result.data?.description) {
        const formattedDescription = formatGeneratedContent(result.data.description) as string
        updates.activitySummary = formattedDescription
      }

      // Handle gallery/images
      if (result.data?.gallery && Array.isArray(result.data.gallery)) {
        const imageUrls = result.data.gallery
          .filter((item: any) => item.type === 'image' && item.url)
          .map((item: any) => item.url)
        if (imageUrls.length > 0) {
          updates.uploadedImages = imageUrls
        }
      }

      // Handle Step 4 data from options (if available)
      if (
        result.data?.options &&
        Array.isArray(result.data.options) &&
        result.data.options.length > 0
      ) {
        const option = result.data.options[0] // Use first option

        // Package name from option name
        if (option.name) {
          updates.packageName = option.name
        }

        // Subtitle as short description
        if (option.subtitle) {
          updates.shortDescription = option.subtitle
        }

        // What's included
        if (option.included && Array.isArray(option.included)) {
          updates.whatsIncluded = option.included
        }

        // What's not included (excluded)
        if (option.excluded && Array.isArray(option.excluded)) {
          updates.whatsNotIncluded = option.excluded
        }

        // Important information
        if (option.important_info && Array.isArray(option.important_info)) {
          updates.importantInfo = option.important_info
        }

        // Pricing
        if (option.adult_price) {
          updates.pricePerPerson = option.adult_price.toString()
        }
        if (option.child_price) {
          updates.pricePerChild = option.child_price.toString()
        }

        // Extra expenses
        if (option.extra_expenses) {
          updates.extraExpenses = option.extra_expenses
        }

        // Discount
        if (option.discount_price) {
          updates.discount = option.discount_price.toString()
        }

        // Meeting points
        if (option.meeting_points && Array.isArray(option.meeting_points)) {
          const meetingPoints = option.meeting_points.map((point: any) => ({
            title: point.title || '',
            subtitle: point.subtitle || '',
            lat: point.lat || null,
            long: point.long || null,
            details: point.details || '',
          }))
          updates.meetingPoints = meetingPoints
        }

        // Itineraries
        if (option.itinerary && Array.isArray(option.itinerary)) {
          const itineraries = option.itinerary.map((item: any) => ({
            title: item.title || '',
            duration: item.duration || '',
            details: item.subtitle || '',
            hasPhoto: (item.images && item.images.length > 0) || false,
            images: item.images || [],
          }))
          updates.itineraries = itineraries
        }

        // Schedule
        if (option.schedule) {
          if (option.schedule.subtitles && Array.isArray(option.schedule.subtitles)) {
            updates.schedule = option.schedule.subtitles.join('\n')
          }
          if (option.schedule.images && Array.isArray(option.schedule.images)) {
            updates.scheduleImages = option.schedule.images
            updates.scheduleHasPhoto = option.schedule.images.length > 0
          }
        }

        // Auto-open toggles in Step 4 when AI generates content
        if (option.itinerary && Array.isArray(option.itinerary) && option.itinerary.length > 0) {
          updates.accordionOpen = true
        }
        if (option.schedule) {
          updates.scheduleAccordionOpen = true
        }
      }

      if (Object.keys(updates).length === 0) {
        message.warning('No content was generated. Please try again.')
        return
      }

      updateFormData(updates)
      message.success('All content generated successfully!')
    } catch (err: any) {
      console.error(
        'Generation error:',
        err.message || 'An error occurred while generating content',
      )

      if (err.message.includes('401') || err.message.includes('403')) {
        message.error('Authentication failed. Please log in again.')
      } else if (err.message.includes('404')) {
        message.error('Content generation service not found. Please contact support.')
      } else if (err.message.includes('500')) {
        message.error('Server error occurred. Please try again later.')
      } else if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        message.error('Network error. Please check your internet connection and try again.')
      } else {
        message.error(err.message || 'Failed to generate content. Please try again.')
      }
    }
  }

  const handleGenerateAll = async () => {
    setGeneratingAll(true)
    await generateAllContent()
    setGeneratingAll(false)
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) {
      return
    }

    if (!userLoggedIn?.userToken) {
      setUploadError('Authentication token is missing. Please log in again.')
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png']
    const maxFileSize = 20 * 1024 * 1024

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`Invalid file type for ${file.name}. Please upload JPEG or PNG images only.`)
        return
      }
      if (file.size > maxFileSize) {
        setUploadError(`File ${file.name} exceeds the 20MB limit.`)
        return
      }
    }

    setUploading(true)
    setUploadError(null)

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData()
        formDataUpload.append('fileName', file)
        formDataUpload.append('deviceType', 'WEBSITE')
        formDataUpload.append('project', 'cms')

        const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
          method: 'POST',
          headers: {
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: formDataUpload,
        })

        if (!response.ok) {
          throw new Error(`Upload failed for ${file.name}`)
        }

        const result = await response.json()
        if (result.result && result.fileUrl) {
          return result.fileUrl
        } else {
          throw new Error(result.message || `Upload failed for ${file.name}`)
        }
      })

      const uploadedUrls = await Promise.all(uploadPromises)
      const currentImages = formData.uploadedImages || []
      updateFormData({ uploadedImages: [...currentImages, ...uploadedUrls] })
    } catch (err: any) {
      setUploadError(err.message || 'An error occurred during upload')
    } finally {
      setUploading(false)
    }
  }

  const handlePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleRemovePhoto = (indexToRemove: number) => {
    const currentImages = formData.uploadedImages || []
    const newImages = currentImages.filter((_, index) => index !== indexToRemove)
    updateFormData({ uploadedImages: newImages })
    setUploadError(null)
  }

  const handleSubmit = () => {
    onNext()
  }

  const canProceed =
    formData.activityHighlights?.some((h) => h.trim() !== '') &&
    formData.activitySummary?.trim() !== ''

  return (
    <>
      {/* Full Page Loading Overlay */}
      {generatingAll && (
        <>
          <style>
            {`
              @keyframes fadeInModal {
                from {
                  opacity: 0;
                  backdrop-filter: blur(0px);
                }
                to {
                  opacity: 1;
                  backdrop-filter: blur(4px);
                }
              }

              @keyframes scaleInBounce {
                0% {
                  opacity: 0;
                  transform: scale(0.8) translateY(20px);
                }
                60% {
                  transform: scale(1.05) translateY(-5px);
                }
                100% {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }

              @keyframes sparkleRotate {
                0% {
                  transform: rotate(0deg) scale(1);
                }
                25% {
                  transform: rotate(90deg) scale(1.1);
                }
                50% {
                  transform: rotate(180deg) scale(1);
                }
                75% {
                  transform: rotate(270deg) scale(1.1);
                }
                100% {
                  transform: rotate(360deg) scale(1);
                }
              }

              @keyframes pulse {
                0%, 100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.6;
                }
              }

              @keyframes dots {
                0%, 20% {
                  color: transparent;
                  text-shadow: .25em 0 0 transparent, .5em 0 0 transparent;
                }
                40% {
                  color: #667085;
                  text-shadow: .25em 0 0 transparent, .5em 0 0 transparent;
                }
                60% {
                  text-shadow: .25em 0 0 #667085, .5em 0 0 transparent;
                }
                80%, 100% {
                  text-shadow: .25em 0 0 #667085, .5em 0 0 #667085;
                }
              }

              @keyframes progressBar {
                0% {
                  background-position: -200% 0;
                }
                100% {
                  background-position: 200% 0;
                }
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              animation: 'fadeInModal 0.3s ease-out forwards',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '16px',
                textAlign: 'center',
                maxWidth: '300px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                animation: 'scaleInBounce 0.5s ease-out forwards',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <div
                style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  animation: 'sparkleRotate 2s infinite ease-in-out',
                  display: 'inline-block',
                }}
              >
                ✨
              </div>
              <h3
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#212427',
                  fontFamily: 'Poppins, sans-serif',
                  animation: 'pulse 1.5s infinite ease-in-out',
                }}
              >
                Generating with AI
              </h3>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  color: '#667085',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Please wait while we generate content for your activity
                <span
                  style={{
                    animation: 'dots 1.5s infinite',
                    fontSize: '14px',
                  }}
                >
                  ...
                </span>
              </p>

              {/* Progress indicator */}
              <div
                style={{
                  marginTop: '20px',
                  height: '4px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #0048B3, #4285F4, #0048B3)',
                    backgroundSize: '200% 100%',
                    animation: 'progressBar 1.5s infinite ease-in-out',
                    borderRadius: '2px',
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto',
          boxSizing: 'border-box',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              fontSize: '14px',
              color: '#667085',
              marginBottom: '8px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            STEP 3 OF 5
          </div>
          <h2
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#212427',
              margin: '0',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Add activity details
          </h2>
          {/* <div style={{ marginTop: '16px' }}>
            <button
              onClick={handleGenerateAll}
              disabled={generatingAll}
              style={{
                padding: '12px 24px',
                backgroundColor: generatingAll ? '#D6DAE0' : '#0048B3',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                cursor: generatingAll ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {generatingAll ? '✨ Generating...' : '✨ Generate with AI'}
            </button>
          </div> */}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '16px',
              color: '#212427',
              fontWeight: '500',
              fontFamily: 'Poppins, sans-serif',
              marginBottom: '8px',
            }}
          >
            Activity highlight <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <p
            style={{
              fontSize: '14px',
              color: '#667085',
              marginTop: '4px',
              marginBottom: '16px',
            }}
          >
            Provide a few key highlights, one per line.
          </p>

          {(formData.activityHighlights || []).map((highlight, index) => (
            <div key={index} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  value={highlight}
                  onChange={(e) => handleHighlightChange(index, e.target.value)}
                  placeholder={`Highlight ${index + 1}`}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D6DAE0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#212427',
                    outline: 'none',
                    fontFamily: 'Poppins, sans-serif',
                    boxSizing: 'border-box',
                  }}
                />
                {formData.activityHighlights.length > 1 && (
                  <button
                    onClick={() => handleRemoveHighlight(index)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      marginLeft: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#667085',
                  textAlign: 'right',
                  marginTop: '4px',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {highlight.length}/500 characters
              </div>
            </div>
          ))}

          <button
            onClick={handleAddHighlight}
            style={{
              marginTop: '8px',
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#212427',
              border: '1px solid #D6DAE0',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            + Add another highlight
          </button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '16px',
              color: '#212427',
              fontWeight: '500',
              fontFamily: 'Poppins, sans-serif',
              marginBottom: '8px',
            }}
          >
            Activity summary <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea
            value={formData.activitySummary || ''}
            onChange={(e) => handleSummaryChange(e.target.value)}
            placeholder="Write a summary of this activity"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #D6DAE0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#212427',
              resize: 'vertical',
              outline: 'none',
              minHeight: '80px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
            rows={3}
          />
          <div
            style={{
              fontSize: '12px',
              color: '#667085',
              textAlign: 'right',
              marginTop: '4px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            {(formData.activitySummary || '').length}/500 characters
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '16px',
              color: '#212427',
              marginBottom: '16px',
              fontWeight: '500',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Photos of activity <span style={{ color: '#667085' }}>(optional)</span>
          </label>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png"
            multiple
            style={{ display: 'none' }}
          />

          {(formData.uploadedImages || []).length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '12px',
                  marginBottom: '12px',
                }}
              >
                {(formData.uploadedImages || []).map((imageUrl, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      aspectRatio: '1',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Uploaded ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        color: 'white',
                        cursor: 'pointer',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                      }}
                      disabled={uploading}
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            onClick={handlePhotoClick}
            style={{
              border: '2px dashed #D6DAE0',
              borderRadius: '8px',
              padding: '32px 24px',
              textAlign: 'center',
              backgroundColor: '#F9FAFB',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>{uploading ? '⏳' : '📷'}</div>
            <p
              style={{
                color: '#0048B3',
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                margin: '0 0 8px 0',
              }}
            >
              {uploading ? 'Uploading...' : 'Upload or take a photo'}
            </p>
            <p
              style={{
                color: '#667085',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                margin: '0',
              }}
            >
              You can select multiple photos at once
            </p>
            {(formData.uploadedImages || []).length > 0 && (
              <p
                style={{
                  color: '#10B981',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  margin: '8px 0 0 0',
                  fontWeight: '500',
                }}
              >
                {(formData.uploadedImages || []).length} photo
                {(formData.uploadedImages || []).length > 1 ? 's' : ''} uploaded
              </p>
            )}
            {uploadError && (
              <p
                style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '8px',
                  margin: '8px 0 0 0',
                }}
              >
                {uploadError}
              </p>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#667085',
              border: '1px solid #D6DAE0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            ← Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={!canProceed || uploading}
            style={{
              padding: '12px 24px',
              backgroundColor: canProceed && !uploading ? '#0048B3' : '#D6DAE0',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: canProceed && !uploading ? 'pointer' : 'not-allowed',
              fontWeight: '500',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Next
          </button>
        </div>
      </div>
    </>
  )
}

export default Step3
